import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Title, Meta } from '@angular/platform-browser';
import {
  LucideAngularModule,
  ShoppingCartIcon,
  PlusIcon,
  CheckIcon,
} from 'lucide-angular';
import { MainLayout } from '../../layouts/main-layout';
import { CategoriesService } from '../../services/categories.service';
import { CartService } from '../../services/cart.service';
import { ProductsPage } from '../../components/shop/products-page/products-page.component';
import { ModelosSeleccionComponent } from '../../components/shop/modelos-seleccion/modelos-seleccion.component';
import type { Category } from '../../types/category.types';
import { smartTitle } from '../../utils/pipes/smart-capitalize';
import { ProductService } from '../../services/product.service';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { NotificationService } from '../../services/notification.service';
import { VehicleService } from '../../services/vehicle.service';
import { getModelsByBrand } from '../../utils/vehicles/get-vehicles';
import { ModeloVehiculo } from '../../types/vehicle.types';
import { getGroupDescription } from '../../utils/groups-descriptions/get-group-description';
import { fromSlug, toSlug } from '../../utils/slugs/slug.utils';
import { SeoContentService } from '../../services/seo-content.service';
import { SeoContent } from '../../types/seo-content.types';

interface VehicleFilter {
  marca?: string;
  modelo?: string;
  year?: string;
  motor?: string;
}

@Component({
  selector: 'allas-categoria-dinamica',
  imports: [
    MainLayout,
    CommonModule,
    RouterModule,
    FormsModule,
    LucideAngularModule,
    ProductsPage,
    ModelosSeleccionComponent,
    MarginWrapper,
  ],
  template: `
    <!-- Titulo Principal oculto pero indexable -->
    <h1 class="absolute left-[-9999px]">
      {{ h1Title() }}
    </h1>
    <allas-main-layout>
      <allas-margin-wrapper styleClass="fs-condensed my-8">
        <!-- Breadcrumb -->
        <div
          class="flex flex-wrap items-center text-sm text-gray-600 mb-6 gap-y-2"
        >
          <a routerLink="/" class="hover:text-blue-600">Inicio</a>
          <svg
            class="w-4 h-4 mx-2 hidden sm:block"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
          <span class="mx-2 block sm:hidden">/</span>
          <a [routerLink]="getRepuestosLink()" class="hover:text-allasRed">
            Repuestos
          </a>
          @for (breadcrumb of breadcrumbs(); track breadcrumb.id) {
          <svg
            class="w-4 h-4 mx-2 hidden sm:block"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
          <span class="mx-2 block sm:hidden">/</span>
          @if ($last) {
          <span class="text-gray-900 font-medium break-all">
            {{ breadcrumb.name }}
          </span>
          } @else {
          <a
            [routerLink]="getCategoryLink(breadcrumb.slug)"
            class="hover:text-allasRed break-all"
          >
            {{ breadcrumb.name }}
          </a>
          } }
        </div>

        <!-- Popular Parts Section -->
        @if (popularParts().length > 0 && !isLeafCategory()) {
        <div class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 mb-6">
            <!---->
            {{ currentCategory()?.name?.toUpperCase() }} POPULARES
            <!---->
            @if (getVehicleText()) {
            <span class="text-red-600">
              PARA {{ getVehicleText().toUpperCase() }}</span
            >
            }
          </h2>
          <div class="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
            @for (part of popularParts().slice(0, 11); track part.id) {
            <a
              [routerLink]="getCategoryLink(part.slug)"
              class="group bg-white rounded-lg border-[1px] border-gray-200 p-4 hover:border-red-300 hover:shadow-lg transition-colors duration-300"
            >
              <div
                class="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden"
              >
                <img
                  [src]="part.url_imagen"
                  [alt]="part.name"
                  onerror="this.src='/placeholder.svg'"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 bg-white"
                  width="300"
                  height="300"
                  loading="lazy"
                />
              </div>
              <div
                class="text-sm font-medium text-gray-800 text-center leading-tight group-hover:text-red-600 transition-colors"
              >
                {{ part.name }} @if (marca()) { para
                <span class="text-red-600"> {{ smartTitle(marca()) }}</span>
                } @if (modelo()) {
                <span class="text-red-600"> {{ smartTitle(modelo()) }}</span>
                } @if (anio()) {
                <span class="text-red-600"> {{ anio() }}</span>
                }
              </div>
            </a>
            }

            <!-- Liquidación Card -->
            <a
              href="/bajo-construccion"
              class="group bg-white rounded-lg border border-gray-200 p-4 hover:border-red-300 hover:shadow-lg transition-all duration-300"
              aria-label="Ver productos en liquidación"
            >
              <div
                class="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center overflow-hidden"
              >
                <img
                  src="/images/home/clearance.webp"
                  alt="Productos en liquidación"
                  onerror="this.src='/placeholder.svg'"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 bg-white"
                />
              </div>
              <div
                class="text-sm font-medium text-gray-800 text-center leading-tight group-hover:text-red-600 transition-colors"
              >
                Liquidación @if (marca()) { para
                <span class="text-red-600"> {{ smartTitle(marca()) }}</span>
                } @if (modelo()) {
                <span class="text-red-600"> {{ smartTitle(modelo()) }}</span>
                }
              </div>
            </a>
          </div>
        </div>
        }

        <!-- Subcategories Grid -->
        @if (subcategories().length > 0) {
        <div class="mb-12">
          <h2 class="text-2xl font-bold text-gray-900 mb-6">
            <!---->
            @if (!isInternalCategory()) {
            <!---->
            COMPRAR POR SUBCATEGORÍA
            <!---->
            } @else {
            <!---->
            COMPRAR POR PRODUCTO
            <!---->
            }
            <!---->
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            @for (subcategory of subcategories(); track subcategory.id) {
            <a
              [routerLink]="getCategoryLink(subcategory.slug)"
              class="group flex justify-between bg-white rounded-lg border border-gray-200 p-4 hover:border-red-300 hover:shadow-lg transition-all duration-300"
            >
              <div class="flex items-center justify-between w-full">
                <div
                  class="text-sm font-medium text-gray-800 leading-tight group-hover:text-red-600 transition-colors"
                >
                  <!---->
                  {{ subcategory.name }}
                  <!---->
                  @if (marca()) { para
                  <span class="text-red-600"> {{ smartTitle(marca()) }}</span>
                  } @if (modelo()) {
                  <span class="text-red-600">
                    {{ smartTitle(modelo()) }}
                  </span>
                  } @if (anio()) {
                  <span class="text-red-600"> {{ anio() }}</span>
                  }
                </div>
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </div>
            </a>
            }
          </div>
        </div>
        }

        <!-- Modelos Selection -->
        @if (marca() && !modelo() && modelos().length > 0 && !isLeafCategory())
        {
        <allas-modelos-seleccion [marca]="marca" [modelos]="modelos" />
        }

        <!-- Leaf Category - Products or Coming Soon -->
        @if (isLeafCategory()) {
        <!-- Products Page -->
        <allas-products-page [h1DescriptionTitle]="h1DescriptionTitle()" />
        }

        <!-- SEO Content -->
        <div
          class="bg-white rounded-xl border border-gray-200 p-8 flex flex-col md:flex-row gap-2 mt-4"
        >
          <div class="text-2xl font-bold text-gray-900 w-2/4 text-right">
            <h2>
              {{ getSEOTitle() }}
            </h2>
          </div>
          <div
            class="prose prose-gray text-gray-700 flex-auto flex flex-col items-center"
          >
            <div
              class="w-3/4 [&_h2]:font-bold [&_h3]:font-bold [&_h2]:my-2 [&_h3]:my-2 [&_h2]:mt-6 [&_h3]:mt-6"
            >
              <p [innerHTML]="groupDescription()"></p>
            </div>
          </div>
        </div>
      </allas-margin-wrapper>
    </allas-main-layout>
  `,
})
export class CategoriaDinamica implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private meta = inject(Meta);
  categoriesService = inject(CategoriesService);
  cartService = inject(CartService);
  vehicleService = inject(VehicleService);
  productService = inject(ProductService);
  smartTitle = smartTitle;
  notificationService = inject(NotificationService);
  seoContentService = inject(SeoContentService);
  seoContent = signal<SeoContent | null>(null);

  // Icons
  readonly shoppingCartIcon = ShoppingCartIcon;
  readonly plusIcon = PlusIcon;
  readonly checkIcon = CheckIcon;

  // Estado para el botón de agregar al carrito
  addingToCart = signal(false);

  currentSlug = signal<string>('');
  currentCategory = signal<Category | null>(null);
  groupDescription = signal<string | null>(null);
  subcategories = signal<Category[]>([]);
  popularParts = signal<Category[]>([]);
  breadcrumbs = signal<Category[]>([]);
  relatedCategories = signal<Category[]>([]);
  vehicleFilter: VehicleFilter = {};
  hasProducts = signal<boolean>(false);

  showVehicleFilter = computed(
    () => this.currentCategory() && !this.isLeafCategory()
  );

  marca = signal<string>('');
  modelo = signal<string>('');
  anio = signal<string>('');
  modelos = signal<ModeloVehiculo[]>([]);

  actualRoute = signal<string>('');

  esOProvieneDeLubricantesHerramientasYAccesorios = signal<boolean>(false);
  h1Title = signal<string>('');
  h1DescriptionTitle = signal<string>('');

  ngOnInit() {
    const actualRoute = this.route.snapshot.url.join('/');
    this.actualRoute.set(actualRoute);
    this.getSeoContent();

    this.route.params.subscribe((params) => {
      // Detectar marca y modelo desde los parámetros de ruta
      this.marca.set(fromSlug(params['marca'] || ''));
      this.modelo.set(fromSlug(params['modelo'] || ''));
      this.anio.set(params['anio'] || '');
      this.vehicleFilter = {
        marca: fromSlug(params['marca'] || ''),
        modelo: fromSlug(params['modelo'] || ''),
        year: params['anio'],
        motor: params['motor'],
      };

      const slug = params['slug'];

      if (slug) {
        this.loadCategory(slug);
      }
      // Cargar modelos si hay marca pero no modelo
      if (params['marca'] && !params['modelo']) {
        this.modelos.set(getModelsByBrand(fromSlug(params['marca'] || '')));
      } else {
        this.modelos.set([]);
      }

      // Configurar meta tags después de cargar la categoría
      this.setMetaTags();
    });
  }

  private getSeoContent() {
    this.seoContentService
      .getSeoContent(this.actualRoute())
      .subscribe((seoContent) => {
        if (seoContent) {
          this.seoContent.set(seoContent);
          this.setMetaTags();
          this.groupDescription.set(seoContent.history);
          this.h1DescriptionTitle.set(seoContent.seoSubtitle);
        }
      });
  }

  private async loadCategory(slug: string) {
    this.currentSlug.set(slug);

    const category = this.categoriesService.findCategoryBySlug(slug);
    if (!category) {
      this.router.navigate(['/repuestos']);
      return;
    }

    this.currentCategory.set(category);

    if (!this.seoContent()) {
      this.groupDescription.set(
        getGroupDescription(category.id, category.name, this.getVehicleText())
      );
    }

    const childrenSorted = category.children?.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    this.subcategories.set(childrenSorted || []);
    this.popularParts.set(this.categoriesService.getPopularParts(slug));
    this.breadcrumbs.set(this.categoriesService.getCategoryPath(slug));
    this.relatedCategories.set(
      this.categoriesService.getSiblingCategories(slug)
    );
    this.setEsOProvieneDeLubricantesHerramientasYAccesorios();
  }

  private setEsOProvieneDeLubricantesHerramientasYAccesorios() {
    const categoryParents = this.categoriesService.getCategoryAndParents(
      this.currentSlug()
    );

    const [firstParent] = categoryParents;

    if (!firstParent) return;

    const ids = [10000, 10001, 10002];

    if (ids.includes(firstParent?.id)) {
      this.esOProvieneDeLubricantesHerramientasYAccesorios.set(true);
    }
  }

  private setMetaTags() {
    const route = this.router.url;
    const category = this.currentCategory();
    const vehicleText = smartTitle(this.getVehicleText() || '');
    if (!category) return;

    const categoryName = category.name.toLowerCase();

    // Crear título dinámico
    let pageTitle: string;
    let description: string;
    let keywords: string;

    if (
      this.isLeafCategory() ||
      this.esOProvieneDeLubricantesHerramientasYAccesorios()
    ) {
      const title = `${category.name}`;
      pageTitle = `${title} para ${vehicleText || 'Carro'} - Allas Repuestos`;
      this.h1Title.set(`${title} para ${vehicleText || 'vehículo'}`);
      description = `Encontrá y Cotizá ${title} para tu ${
        vehicleText || 'Carro'
      } a un precio accesible, calidad garantizada y con envío a todo Honduras!`;
    } else {
      const title = `Repuestos de ${category.name}`;
      pageTitle = `${title} para ${vehicleText || 'Carro'} - Allas Repuestos`;
      this.h1Title.set(`${title} para ${vehicleText || 'vehículo'}`);
      description = `Encontrá y Cotizá ${title} para tu ${
        vehicleText || 'Carro'
      } a un precio accesible, calidad garantizada y con envío a todo Honduras!`;
    }

    keywords = `${categoryName}, repuestos, ${vehicleText}, envío a domicilio, Honduras, garantía, calidad, Allas Repuestos`;

    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    if (this.seoContent()) {
      pageTitle = this.seoContent()?.seoTitle || pageTitle;
      description = this.seoContent()?.seoDescription || description;
      keywords = this.seoContent()?.seoKeywords || keywords;
    }

    // --- Título de la página ---
    this.title.setTitle(pageTitle);

    // --- Meta Descripción ---
    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    // --- Palabras Clave ---
    this.meta.updateTag({
      name: 'keywords',
      content: keywords,
    });

    // --- Open Graph Tags ---
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });

    this.meta.updateTag({ property: 'og:url', content: route });

    // --- Twitter Tags ---
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // --- Icono del sitio (favicon / logo principal) ---
    this.meta.updateTag({
      name: 'icon',
      content: imageUrl,
    });
  }

  isLeafCategory(): boolean {
    if (!this.currentCategory()) return false;
    return this.categoriesService.isLeafCategory(this.currentCategory()!);
  }

  isMainCategory(): boolean {
    return this.breadcrumbs().length === 1;
  }

  isInternalCategory(): boolean {
    return this.breadcrumbs().length === 2;
  }

  getSEOTitle(): string {
    if (this.seoContent()) {
      return this.seoContent()?.seoTitle || '';
    }

    const category = this.currentCategory();
    if (!category) return '';

    const vehicleText = this.getVehicleText();
    if (vehicleText) {
      return `${category.name} para ${vehicleText} en Honduras`;
    }
    return `${category.name} en Honduras`;
  }

  getVehicleText(): string {
    const parts: string[] = [];
    if (this.vehicleFilter.marca)
      parts.push(smartTitle(this.vehicleFilter.marca));
    if (this.vehicleFilter.modelo)
      parts.push(smartTitle(this.vehicleFilter.modelo));
    if (this.vehicleFilter.year) parts.push(this.vehicleFilter.year);

    return parts.join(' ') || '';
  }

  getCurrentQueryParams(): any {
    const params: any = {};
    if (this.vehicleFilter.marca) params.marca = this.vehicleFilter.marca;
    if (this.vehicleFilter.modelo) params.modelo = this.vehicleFilter.modelo;
    if (this.vehicleFilter.year) params.year = this.vehicleFilter.year;
    if (this.vehicleFilter.motor) params.motor = this.vehicleFilter.motor;
    return params;
  }

  getRepuestosLink(): string[] {
    if (this.marca() && this.modelo()) {
      return ['/repuestos', 'v', toSlug(this.marca()), toSlug(this.modelo())];
    } else if (this.marca()) {
      return ['/repuestos', 'v', toSlug(this.marca())];
    } else {
      return ['/repuestos'];
    }
  }

  getCategoryLink(slug: string): string[] {
    if (this.marca() && this.modelo() && this.anio()) {
      return [
        '/repuestos',
        slug,
        toSlug(this.marca()),
        toSlug(this.modelo()),
        this.anio(),
      ];
    } else if (this.marca() && this.modelo()) {
      return ['/repuestos', slug, toSlug(this.marca()), toSlug(this.modelo())];
    } else if (this.marca()) {
      return ['/repuestos', slug, toSlug(this.marca())];
    } else {
      return ['/repuestos', slug];
    }
  }

  onVehicleFilterChange() {
    // Con las nuevas rutas, necesitamos navegar a la ruta completa
    const currentSlug = this.currentSlug();
    if (currentSlug) {
      const route = this.getCategoryLink(currentSlug);
      this.router.navigate(route);
    }
  }

  clearVehicleFilter() {
    this.vehicleFilter = {};
    this.marca.set('');
    this.modelo.set('');
    const currentSlug = this.currentSlug();
    if (currentSlug) {
      this.router.navigate(['/repuestos', currentSlug]);
    }
  }

  getYears(): string[] {
    return Array.from({ length: 25 }, (_, i) => (2025 - i).toString());
  }

  goBack() {
    const breadcrumbs = this.breadcrumbs();
    if (breadcrumbs.length > 1) {
      const parentSlug = breadcrumbs[breadcrumbs.length - 2].slug;
      const route = this.getCategoryLink(parentSlug);
      this.router.navigate(route);
    } else {
      const route = this.getRepuestosLink();
      this.router.navigate(route);
    }
  }

  /**
   * Agrega la categoría actual al carrito
   */
  addToCart(): void {
    const category = this.currentCategory();
    if (!category) return;

    this.addingToCart.set(true);

    // Simular un pequeño delay para mostrar el estado de loading
    setTimeout(() => {
      this.cartService.addItemToCart({
        categoria: category,
        cantidad: 1,
      });
      this.addingToCart.set(false);

      // Mostrar notificación de éxito
      this.showSuccessNotification();
    }, 800);
  }

  /**
   * Incrementa la cantidad en el carrito
   */
  increaseCartQuantity(): void {
    const category = this.currentCategory();
    if (!category) return;

    this.cartService.addItemToCart({
      categoria: category,
      cantidad: 1,
    });
    this.showSuccessNotification();
  }

  /**
   * Muestra una notificación de éxito temporal
   */
  private showSuccessNotification(): void {
    // Aquí podrías integrar un servicio de notificaciones
    // Por ahora usamos un alert simple
    const categoryName = this.currentCategory()?.name || '';
    this.notificationService.showSuccess(
      `${categoryName} agregado al carrito de cotización!`
    );
  }
}
